import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";
import {
  LogOut,
  Mail,
  Check,
  Circle,
  Trash2,
  ChevronDown,
  RefreshCw,
  ShieldCheck,
  Inbox,
  Send,
  FileText,
  MousePointerClick,
  Copy,
  ExternalLink,
  Radar,
} from "lucide-react";
import MailingPage from "@/components/mailing/MailingPage";
import LeadScraperPage from "@/components/leads/LeadScraperPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAB_LABELS = {
  inquiries: "Inquiries",
  iec: "Registry Requests",
  traffic: "Traffic",
  leads: "Leads",
  mailing: "Mailing",
};

const authHeader = async () => {
  if (!auth.currentUser) {
    throw new Error("Missing Firebase admin session");
  }
  const token = await auth.currentUser.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const token = await credential.user.getIdToken();
      await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
      onLogin();
      toast.success("Access granted.");
    } catch (err) {
      await signOut(auth).catch(() => {});
      toast.error(err?.response?.data?.detail || "Invalid Firebase admin credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center p-6 bg-[#FAF3E0] velora-grain relative"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md bg-[#FFFFFF] border border-[#E0D4B6] p-10 md:p-12"
      >
        <div className="flex items-center gap-4 mb-10">
          <img
            src="/logo.png"
            alt="Vilora Impex"
            className="w-14 h-14 object-contain"
            style={{ filter: "drop-shadow(0 0 16px rgba(232,148,58,0.6))" }}
          />
          <div>
            <div className="velora-serif text-lg text-[#3D1F00] tracking-tight">Vilora Impex</div>
            <div className="velora-meta text-[#7B3F00]/80">Admin Console</div>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-8 h-[1px] bg-[#E8943A]" />
          <span className="velora-micro text-[#7B3F00]">Firebase Access</span>
        </div>
        <h1 className="velora-serif text-4xl text-[#3D1F00] tracking-tight leading-tight mb-8">
          Sign in to the<br />
          <span className="italic text-[#7B3F00] font-light">admin console.</span>
        </h1>

        <form onSubmit={submit} className="space-y-8">
          <div>
            <label className="velora-micro text-[#3D1F00]/50 block mb-2">Email</label>
            <input
              data-testid="admin-email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@viloraimpex.com"
              className="velora-input"
            />
          </div>
          <div>
            <label className="velora-micro text-[#3D1F00]/50 block mb-2">Password</label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="velora-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="velora-btn-primary w-full justify-center disabled:opacity-50"
          >
            <ShieldCheck size={16} strokeWidth={1.2} />
            <span>{loading ? "Verifying..." : "Sign In"}</span>
          </button>
        </form>

        <div className="hairline mt-10" />
        <a
          href="/"
          className="velora-micro text-[#3D1F00]/50 hover:text-[#7B3F00] transition mt-6 inline-block"
        >
          ← Back to Vilora Impex
        </a>
      </motion.div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#E0D4B6] bg-[#FFFFFF] p-6">
      <div className="velora-micro text-[#3D1F00]/50">{label}</div>
      <div className="velora-serif text-5xl text-[#7B3F00] mt-3 leading-none">{value}</div>
    </div>
  );
}

function InquiryRow({ inq, onToggleRead, onDelete }) {
  const [open, setOpen] = useState(false);
  const created = new Date(inq.created_at);
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`border-b border-[#E0D4B6]/50 ${!inq.read ? "bg-[#FFFFFF]" : ""}`}
      data-testid={`inquiry-row-${inq.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full grid grid-cols-12 gap-4 items-center px-4 md:px-6 py-5 text-left hover:bg-[#FFFFFF]/70 transition-colors"
      >
        <div className="col-span-1 flex justify-center">
          {inq.read ? (
            <Check size={14} strokeWidth={1.3} className="text-[#E0D4B6]" />
          ) : (
            <Circle size={10} strokeWidth={0} fill="#E8943A" className="text-[#7B3F00]" />
          )}
        </div>
        <div className="col-span-11 md:col-span-3">
          <div className="velora-serif text-lg text-[#3D1F00] tracking-tight">{inq.name}</div>
          <div className="velora-sans text-xs text-[#3D1F00]/50 mt-1">{inq.company || "-"}</div>
        </div>
        <div className="hidden md:block md:col-span-3 velora-sans text-sm text-[#3D1F00]/70">
          <span
            role="link"
            tabIndex={0}
            className="inline-block max-w-full truncate hover:text-[#7B3F00] transition-colors"
            title={inq.email}
            onClick={(event) => {
              event.stopPropagation();
              window.location.href = `mailto:${inq.email}`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                window.location.href = `mailto:${inq.email}`;
              }
            }}
          >
            {inq.email}
          </span>
        </div>
        <div className="hidden md:block md:col-span-2">
          <span className="velora-micro text-[#7B3F00]/80">{inq.country || "-"}</span>
        </div>
        <div className="hidden md:block md:col-span-2 velora-sans text-xs text-[#3D1F00]/50">
          {inq.product_interest || "-"}
        </div>
        <div className="hidden md:flex md:col-span-1 justify-end">
          <ChevronDown
            size={16}
            strokeWidth={1.2}
            className={`text-[#3D1F00]/50 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden"
          >
            <div className="px-4 md:px-6 pb-6 grid md:grid-cols-12 gap-6">
              <div className="md:col-span-9 md:col-start-2">
                <div className="velora-micro text-[#7B3F00]/70 mb-3">Requirement</div>
                <p className="velora-sans text-[#3D1F00]/85 leading-relaxed whitespace-pre-wrap font-light">
                  {inq.message}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 pt-6 border-t border-[#E0D4B6]/50">
                  <div>
                    <div className="velora-micro text-[#3D1F00]/40">Received</div>
                    <div className="velora-sans text-sm text-[#3D1F00]/75 mt-1">
                      {created.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="velora-micro text-[#3D1F00]/40">Inquiry ID</div>
                    <div className="velora-sans text-sm text-[#3D1F00]/75 mt-1 font-mono">
                      {inq.id.slice(0, 8)}
                    </div>
                  </div>
                  <div className="md:col-span-2 flex items-end justify-start md:justify-end gap-4">
                    <a
                      href={`mailto:${inq.email}?subject=Re: Your Vilora Impex inquiry`}
                      className="velora-btn-ghost"
                      data-testid={`reply-${inq.id}`}
                    >
                      <Mail size={14} strokeWidth={1.2} />
                      Reply
                    </a>
                    <button
                      type="button"
                      onClick={() => onToggleRead(inq)}
                      data-testid={`toggle-read-${inq.id}`}
                      className="velora-btn-ghost"
                    >
                      {inq.read ? "Mark Unread" : "Mark Read"}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(inq)}
                      data-testid={`delete-${inq.id}`}
                      className="velora-btn-ghost !text-[#D4A017] hover:!text-[#7B3F00]"
                    >
                      <Trash2 size={14} strokeWidth={1.2} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function IecRequestRow({ request, onToggleRead, onDelete }) {
  const created = new Date(request.created_at);
  const documentType = request.document_type || "IEC";

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`grid grid-cols-12 gap-4 items-center px-4 md:px-6 py-5 border-b border-[#E0D4B6]/50 ${
        !request.read ? "bg-[#FFFFFF]" : ""
      }`}
      data-testid={`iec-request-row-${request.id}`}
    >
      <div className="col-span-1 flex justify-center">
        {request.read ? (
          <Check size={14} strokeWidth={1.3} className="text-[#E0D4B6]" />
        ) : (
          <Circle size={10} strokeWidth={0} fill="#E8943A" className="text-[#7B3F00]" />
        )}
      </div>
      <div className="col-span-11 md:col-span-4">
        <div className="velora-serif text-xl text-[#3D1F00] tracking-tight">
          {documentType} details request
        </div>
        <div className="velora-sans text-xs text-[#3D1F00]/50 mt-1">
          {created.toLocaleString()}
        </div>
      </div>
      <div className="col-span-12 md:col-span-4 velora-sans text-sm text-[#3D1F00]/70">
        <a
          href={`mailto:${request.email}?subject=Vilora Impex ${documentType} details`}
          className="inline-block max-w-full truncate hover:text-[#7B3F00] transition-colors"
          title={request.email}
        >
          {request.email}
        </a>
      </div>
      <div className="col-span-12 md:col-span-3 flex items-center justify-start md:justify-end gap-3">
        <a
          href={`mailto:${request.email}?subject=Vilora Impex ${documentType} details`}
          className="velora-btn-ghost"
          data-testid={`reply-iec-${request.id}`}
        >
          <Mail size={14} strokeWidth={1.2} />
          Reply
        </a>
        <button
          type="button"
          onClick={() => onToggleRead(request)}
          data-testid={`toggle-iec-${request.id}`}
          className="velora-btn-ghost"
        >
          {request.read ? "Mark Unread" : "Mark Read"}
        </button>
        <button
          type="button"
          onClick={() => onDelete(request)}
          data-testid={`delete-iec-${request.id}`}
          className="velora-btn-ghost !text-[#D4A017] hover:!text-[#7B3F00]"
        >
          <Trash2 size={14} strokeWidth={1.2} />
        </button>
      </div>
    </motion.div>
  );
}

function LinkClickRow({ click }) {
  const created = new Date(click.created_at);
  const visitor = click.ip_hash
    ? `Visitor ${click.ip_hash.slice(0, 6).toUpperCase()}`
    : "Anonymous visitor";
  const referrer = click.referrer || "Direct or app handoff";
  const device = [click.language, click.timezone, click.screen].filter(Boolean).join(" · ");

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-12 gap-4 items-center px-4 md:px-6 py-5 border-b border-[#E0D4B6]/50 bg-[#FFFFFF]/40"
      data-testid={`link-click-row-${click.id}`}
    >
      <div className="col-span-12 md:col-span-3">
        <div className="velora-serif text-xl text-[#3D1F00] tracking-tight capitalize">
          {click.source || "instagram"}
        </div>
        <div className="velora-sans text-xs text-[#3D1F00]/50 mt-1">
          {click.campaign || "bio"} · {created.toLocaleString()}
        </div>
      </div>
      <div className="col-span-12 md:col-span-3">
        <div className="velora-micro text-[#7B3F00]/80">{visitor}</div>
        <div className="velora-sans text-xs text-[#3D1F00]/55 mt-1">{device || "Device details unavailable"}</div>
      </div>
      <div className="col-span-12 md:col-span-3 velora-sans text-sm text-[#3D1F00]/70 min-w-0">
        <div className="truncate" title={referrer}>
          {referrer}
        </div>
        <div className="velora-sans text-xs text-[#3D1F00]/45 mt-1 truncate" title={click.path || "/ig"}>
          {click.path || "/ig"}
        </div>
      </div>
      <div className="col-span-12 md:col-span-3 velora-sans text-xs text-[#3D1F00]/50 md:text-right min-w-0">
        <span className="line-clamp-2" title={click.user_agent || ""}>
          {click.user_agent || "User agent unavailable"}
        </span>
      </div>
    </motion.div>
  );
}

function Dashboard({ onLogout }) {
  const [topTab, setTopTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [iecRequests, setIecRequests] = useState([]);
  const [linkClicks, setLinkClicks] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    iec_requests: 0,
    iec_unread: 0,
    link_clicks: 0,
    instagram_clicks: 0,
    unique_link_clicks: 0,
  });
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [iecFilter, setIecFilter] = useState("all"); // all | unread | read
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeader();
      const [i, iec, clicks, s] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/iec-requests`, { headers }),
        axios.get(`${API}/admin/link-clicks`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setInquiries(i.data);
      setIecRequests(iec.data);
      setLinkClicks(clicks.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        await signOut(auth).catch(() => {});
        onLogout();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load admin dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRead = async (inq) => {
    try {
      const headers = await authHeader();
      const { data } = await axios.patch(
        `${API}/admin/inquiries/${inq.id}`,
        { read: !inq.read },
        { headers },
      );
      setInquiries((cur) => cur.map((x) => (x.id === inq.id ? data : x)));
      setStats((s) => ({
        ...s,
        unread: Math.max(0, s.unread + (data.read ? -1 : 1)),
      }));
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteInq = async (inq) => {
    if (!window.confirm(`Delete inquiry from ${inq.name}?`)) return;
    try {
      const headers = await authHeader();
      await axios.delete(`${API}/admin/inquiries/${inq.id}`, { headers });
      setInquiries((cur) => cur.filter((x) => x.id !== inq.id));
      setStats((s) => ({
        ...s,
        total: s.total - 1,
        unread: s.unread - (inq.read ? 0 : 1),
      }));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const toggleIecRequestRead = async (request) => {
    try {
      const headers = await authHeader();
      const { data } = await axios.patch(
        `${API}/admin/iec-requests/${request.id}`,
        { read: !request.read },
        { headers },
      );
      setIecRequests((cur) => cur.map((x) => (x.id === request.id ? data : x)));
      setStats((s) => ({
        ...s,
        iec_unread: Math.max(0, (s.iec_unread || 0) + (data.read ? -1 : 1)),
      }));
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteIecRequest = async (request) => {
    const documentType = request.document_type || "IEC";
    if (!window.confirm(`Delete ${documentType} request from ${request.email}?`)) return;
    try {
      const headers = await authHeader();
      await axios.delete(`${API}/admin/iec-requests/${request.id}`, { headers });
      setIecRequests((cur) => cur.filter((x) => x.id !== request.id));
      setStats((s) => ({
        ...s,
        iec_requests: Math.max(0, (s.iec_requests || 0) - 1),
        iec_unread: Math.max(0, (s.iec_unread || 0) - (request.read ? 0 : 1)),
      }));
      toast.success("Registry request deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = async () => {
    await signOut(auth).catch(() => {});
    onLogout();
  };

  const filtered = inquiries.filter((i) =>
    filter === "all" ? true : filter === "unread" ? !i.read : i.read,
  );
  const filteredIecRequests = iecRequests.filter((request) =>
    iecFilter === "all" ? true : iecFilter === "unread" ? !request.read : request.read,
  );
  const trackingLink =
    typeof window !== "undefined" ? `${window.location.origin}/ig` : "https://www.viloraimpex.com/ig";
  const copyTrackingLink = async () => {
    try {
      await navigator.clipboard.writeText(trackingLink);
      toast.success("Tracking link copied.");
    } catch {
      window.prompt("Copy tracking link", trackingLink);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF3E0] text-[#3D1F00]">
      {/* Topbar */}
      <header className="border-b border-[#E0D4B6] bg-[#FAF3E0]/95 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Vilora Impex"
              className="w-11 h-11 object-contain"
              style={{ filter: "drop-shadow(0 0 12px rgba(232,148,58,0.55))" }}
            />
            <div>
              <div className="velora-serif text-lg text-[#3D1F00] tracking-tight">Vilora Impex</div>
              <div className="velora-meta text-[#7B3F00]/80">
                Admin · {TAB_LABELS[topTab]}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-1 mr-2">
              <button
                onClick={() => setTopTab("inquiries")}
                className={`velora-micro px-3 py-1 transition flex items-center gap-2 ${topTab === "inquiries" ? "text-[#7B3F00]" : "text-[#3D1F00]/50 hover:text-[#3D1F00]"}`}
              >
                <Inbox size={13} strokeWidth={1.2} /> Inquiries
              </button>
              <button
                onClick={() => setTopTab("iec")}
                className={`velora-micro px-3 py-1 transition flex items-center gap-2 ${topTab === "iec" ? "text-[#7B3F00]" : "text-[#3D1F00]/50 hover:text-[#3D1F00]"}`}
              >
                <FileText size={13} strokeWidth={1.2} /> Registry Requests
              </button>
              <button
                onClick={() => setTopTab("traffic")}
                className={`velora-micro px-3 py-1 transition flex items-center gap-2 ${topTab === "traffic" ? "text-[#7B3F00]" : "text-[#3D1F00]/50 hover:text-[#3D1F00]"}`}
              >
                <MousePointerClick size={13} strokeWidth={1.2} /> Traffic
              </button>
              <button
                onClick={() => setTopTab("leads")}
                className={`velora-micro px-3 py-1 transition flex items-center gap-2 ${topTab === "leads" ? "text-[#7B3F00]" : "text-[#3D1F00]/50 hover:text-[#3D1F00]"}`}
              >
                <Radar size={13} strokeWidth={1.2} /> Leads
              </button>
              <button
                onClick={() => setTopTab("mailing")}
                className={`velora-micro px-3 py-1 transition flex items-center gap-2 ${topTab === "mailing" ? "text-[#7B3F00]" : "text-[#3D1F00]/50 hover:text-[#3D1F00]"}`}
              >
                <Send size={13} strokeWidth={1.2} /> Mailing
              </button>
            </div>
            {!["mailing", "leads"].includes(topTab) && (
              <button
                onClick={load}
                data-testid="admin-refresh"
                className="velora-micro text-[#3D1F00]/60 hover:text-[#7B3F00] transition flex items-center gap-2"
              >
                <RefreshCw size={13} strokeWidth={1.2} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="velora-micro text-[#3D1F00]/60 hover:text-[#7B3F00] transition flex items-center gap-2"
            >
              <LogOut size={13} strokeWidth={1.2} />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14">
        {topTab === "mailing" ? (
          <MailingPage />
        ) : topTab === "leads" ? (
          <LeadScraperPage />
        ) : topTab === "traffic" ? (
          <>
            <div className="flex items-center gap-4 mb-10">
              <div className="w-10 h-[1px] bg-[#E8943A]" />
              <span className="velora-micro text-[#7B3F00]">Instagram Link Tracking</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-10">
              <Stat label="Total Clicks" value={String(stats.link_clicks || 0).padStart(2, "0")} />
              <Stat
                label="Instagram Clicks"
                value={String(stats.instagram_clicks || 0).padStart(2, "0")}
              />
              <Stat
                label="Unique Visitors"
                value={String(stats.unique_link_clicks || 0).padStart(2, "0")}
              />
            </div>

            <div className="border border-[#E0D4B6] bg-[#FFFFFF]/55 p-5 md:p-6 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div className="min-w-0">
                <div className="velora-micro text-[#7B3F00] mb-2">Bio Tracking Link</div>
                <div className="velora-serif text-2xl text-[#3D1F00] tracking-tight break-all">
                  {trackingLink}
                </div>
                <p className="velora-sans text-sm text-[#3D1F00]/60 leading-relaxed mt-3 max-w-2xl">
                  Use this as the Instagram bio URL. Instagram does not expose profile names to websites,
                  so this records anonymous click context only.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={copyTrackingLink} className="velora-btn-primary">
                  <Copy size={14} strokeWidth={1.2} />
                  Copy Link
                </button>
                <a href="/ig" target="_blank" rel="noreferrer" className="velora-btn-ghost">
                  <ExternalLink size={14} strokeWidth={1.2} />
                  Open
                </a>
              </div>
            </div>

            <div className="border border-[#E0D4B6] bg-[#FAF3E0]">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E0D4B6] velora-micro text-[#3D1F00]/40">
                <div className="col-span-3">Source</div>
                <div className="col-span-3">Visitor</div>
                <div className="col-span-3">Referrer / Path</div>
                <div className="col-span-3 text-right">Device</div>
              </div>

              {loading ? (
                <div className="p-16 text-center velora-micro text-[#3D1F00]/40">
                  Loading traffic...
                </div>
              ) : linkClicks.length === 0 ? (
                <div className="p-16 text-center">
                  <MousePointerClick size={30} strokeWidth={1} className="text-[#E0D4B6] mx-auto mb-4" />
                  <div className="velora-micro text-[#3D1F00]/50">
                    No tracked clicks yet. Add the bio link and refresh after a visit.
                  </div>
                </div>
              ) : (
                linkClicks.map((click) => <LinkClickRow key={click.id} click={click} />)
              )}
            </div>
          </>
        ) : topTab === "iec" ? (
          <>
            <div className="flex items-center gap-4 mb-10">
              <div className="w-10 h-[1px] bg-[#E8943A]" />
              <span className="velora-micro text-[#7B3F00]">Registry Request Desk</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-14">
              <Stat label="Registry Requests" value={String(stats.iec_requests || 0).padStart(2, "0")} />
              <Stat label="Unread" value={String(stats.iec_unread || 0).padStart(2, "0")} />
              <Stat
                label="Actioned"
                value={String(
                  Math.max(0, (stats.iec_requests || 0) - (stats.iec_unread || 0)),
                ).padStart(2, "0")}
              />
            </div>

            <div className="flex items-center gap-4 md:gap-6 mb-8 flex-wrap border-b border-[#E0D4B6]/60 pb-4">
              {[
                { id: "all", label: "All" },
                { id: "unread", label: "Unread" },
                { id: "read", label: "Actioned" },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`iec-filter-${f.id}`}
                  onClick={() => setIecFilter(f.id)}
                  className={`velora-micro py-2 px-1 border-b-2 -mb-[17px] transition ${
                    iecFilter === f.id
                      ? "border-[#E8943A] text-[#7B3F00]"
                      : "border-transparent text-[#3D1F00]/50 hover:text-[#3D1F00]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="border border-[#E0D4B6] bg-[#FAF3E0]">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E0D4B6] velora-micro text-[#3D1F00]/40">
                <div className="col-span-1 text-center">-</div>
                <div className="col-span-4">Request</div>
                <div className="col-span-4">Email</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>

              {loading ? (
                <div className="p-16 text-center velora-micro text-[#3D1F00]/40">
                  Loading registry requests…
                </div>
              ) : filteredIecRequests.length === 0 ? (
                <div className="p-16 text-center">
                  <FileText size={30} strokeWidth={1} className="text-[#E0D4B6] mx-auto mb-4" />
                  <div className="velora-micro text-[#3D1F00]/50">No registry requests in this view.</div>
                </div>
              ) : (
                filteredIecRequests.map((request) => (
                  <IecRequestRow
                    key={request.id}
                    request={request}
                    onToggleRead={toggleIecRequestRead}
                    onDelete={deleteIecRequest}
                  />
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-10">
              <div className="w-10 h-[1px] bg-[#E8943A]" />
              <span className="velora-micro text-[#7B3F00]">Operations Overview</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-14">
              <Stat label="Total Inquiries" value={String(stats.total).padStart(2, "0")} />
              <Stat label="Unread" value={String(stats.unread).padStart(2, "0")} />
              <Stat
                label="Actioned"
                value={String(Math.max(0, stats.total - stats.unread)).padStart(2, "0")}
              />
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-4 md:gap-6 mb-8 flex-wrap border-b border-[#E0D4B6]/60 pb-4">
              {[
                { id: "all", label: "All" },
                { id: "unread", label: "Unread" },
                { id: "read", label: "Actioned" },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`velora-micro py-2 px-1 border-b-2 -mb-[17px] transition ${
                    filter === f.id
                      ? "border-[#E8943A] text-[#7B3F00]"
                      : "border-transparent text-[#3D1F00]/50 hover:text-[#3D1F00]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="border border-[#E0D4B6] bg-[#FAF3E0]">
              {/* Header row desktop */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E0D4B6] velora-micro text-[#3D1F00]/40">
                <div className="col-span-1 text-center">-</div>
                <div className="col-span-3">Name / Company</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Country</div>
                <div className="col-span-2">Product</div>
                <div className="col-span-1 text-right">Expand</div>
              </div>

              {loading ? (
                <div className="p-16 text-center velora-micro text-[#3D1F00]/40">
                  Loading inquiries…
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-16 text-center">
                  <Inbox size={30} strokeWidth={1} className="text-[#E0D4B6] mx-auto mb-4" />
                  <div className="velora-micro text-[#3D1F00]/50">No inquiries in this view.</div>
                </div>
              ) : (
                filtered.map((inq) => (
                  <InquiryRow
                    key={inq.id}
                    inq={inq}
                    onToggleRead={toggleRead}
                    onDelete={deleteInq}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAuthed(false);
        setChecking(false);
        return;
      }
      try {
        const headers = await authHeader();
        await axios.get(`${API}/admin/me`, { headers });
        setAuthed(true);
      } catch {
        await signOut(auth).catch(() => {});
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    });
    return unsubscribe;
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAF3E0] flex items-center justify-center">
        <div className="velora-micro text-[#3D1F00]/40">Verifying session…</div>
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={() => setAuthed(false)} />
  ) : (
    <LoginCard onLogin={() => setAuthed(true)} />
  );
}
